import React, { useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { FormControl, FormControlLabel, FormHelperText, Radio, RadioGroup } from '@mui/material';

import { Button } from 'components/Buttons/Button';
import { useAppSelector } from 'hooks/useAppSelector';
import Loader from 'components/Loader';
import * as notificationManager from 'notifications/index';
import { updateRequiredInformation } from 'utils/updateRequiredInformation';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { bankAccountsApi } from 'api/BankAccountsApi';

import { AutopayText } from './autopay.enums';
import styles from './Autopay.module.scss';

interface FormValues {
  isAutopay: boolean | null;
}

const Autopay = () => {
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const { applicationId, variables } = useAppSelector((state) => state.application);
  const { autopayDefaultValue, autopaySectionIsCompleted } = useAppSelector((state) => state.dashboardPortal);

  const {
    formState: { errors, isSubmitted },
    setValue,
    trigger,
    watch,
    control,
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      isAutopay: autopayDefaultValue,
    },
    shouldFocusError: true,
  });
  const watcher = watch();

  const handleAutoPayRadioButtonChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (autopaySectionIsCompleted) {
      return;
    }

    setValue('isAutopay', event.target.value === 'true');

    if (isSubmitted) {
      trigger();
    }
  };

  const saveAutopayValue = handleSubmit(async () => {
    try {
      trigger();

      setIsLoading(true);

      const application = await bankAccountsApi.updateAutopay({
        applicationId: applicationId!,
        autopay: watcher.isAutopay!,
      });

      updateRequiredInformation(application, dispatch);
    } catch (error) {
      notificationManager.notifyFailure(notificationManager.FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  });

  return isLoading ? (
    <Loader customContainerClassname={styles.loader} />
  ) : (
    <>
      <div className={styles.autopayDescription}>
        The rate expressed in your loan offer assume you elect for autopay. If you choose "No" for autopay, your rate
        will be subject a one time increase of {(variables?.autopay_discount || 0) * 100 || '0.00'}% more than the
        original fixed rate expressed in your offer.
      </div>
      <FormControl>
        <Controller
          name="isAutopay"
          control={control}
          rules={{
            validate: (value) => value !== null,
          }}
          render={({ field }) => (
            <RadioGroup
              className={styles.autoPayRadioContainer}
              aria-labelledby="autopay-radio-buttons-group"
              name="autopay-radio-buttons-group"
              value={watcher.isAutopay}
              onChange={handleAutoPayRadioButtonChange}
            >
              <FormControlLabel
                classes={{ label: styles.autoPayRadioLabel }}
                value={true}
                control={
                  <Radio
                    classes={{
                      root: styles.autoPayRadio,
                    }}
                    disabled={autopaySectionIsCompleted}
                  />
                }
                label={AutopayText.Yes}
                inputRef={field.ref}
              />
              <FormControlLabel
                classes={{ label: styles.autoPayRadioLabel }}
                value={false}
                control={
                  <Radio
                    classes={{
                      root: styles.autoPayRadio,
                    }}
                    disabled={autopaySectionIsCompleted}
                  />
                }
                label={AutopayText.No}
              />
            </RadioGroup>
          )}
        />
        {!!errors?.isAutopay && (
          <FormHelperText className={styles.errorMessage}>{AutopayText.ErrorMessage}</FormHelperText>
        )}
      </FormControl>
      {!autopaySectionIsCompleted && (
        <Button className={styles.saveButton} onClick={saveAutopayValue}>
          Save
        </Button>
      )}
    </>
  );
};

export default Autopay;
